import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTranslation } from "@coworker/locales";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Navigate, useParams } from "react-router";
import Linkbar from "../../../shared/components/Linkbar/Linkbar";
import { NotFound } from "../../../task-management/components/NotFound/NotFound";
import { joinClassNames } from "../../components/utils";
import mediaDetailPageSyles from "../media-detail-page.module.css";
import { mediaCards } from "../mock-media";
import { BackButton } from "./BackButton";
import styles from "./service-office-media-detail-page.module.css";
import { DirectTakeawayStep } from "./steps/DirectTakeawayStep";
import { ProductSelectionStep } from "./steps/ProductSelectionStep";
import { WorkBriefStep } from "./steps/WorkBriefStep";
import { useWizard, WizardStep } from "./useWizard";

const STEPS: readonly WizardStep[] = [
  WizardStep.WorkBrief,
  WizardStep.ProductSelection,
  WizardStep.DirectTakeaway,
];

const EMPTY_BACKUPS: Record<string, Item> = {};

const getTranslationKeyByStep = (step: WizardStep | undefined) => {
  if (step === WizardStep.WorkBrief) {
    return "mediaManagementString";
  }
  if (step === WizardStep.ProductSelection) {
    return "workBriefString";
  }
  if (step === WizardStep.DirectTakeaway) {
    return "productSelectionString";
  }
  return "";
};

export const ServiceOfficeMediaDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const media = useMemo(() => mediaCards.find((card) => card.id === id), [id]);

  const linkbarItems = useMemo(
    () => [{ label: t("capitalCaseLaunchMediaString"), to: "/launch-media" }],
    [t]
  );

  const [backupProductsByMedia, setBackupProductsByMedia] = useState<
    Record<string, Record<string, Item>>
  >({});
  const mediaId = id ?? "";
  const backupProducts = backupProductsByMedia[mediaId] ?? EMPTY_BACKUPS;
  const handleBackupProductsChange: Dispatch<
    SetStateAction<Record<string, Item>>
  > = useCallback(
    (update) =>
      setBackupProductsByMedia((current) => {
        const previous = current[mediaId] ?? EMPTY_BACKUPS;
        return {
          ...current,
          [mediaId]: typeof update === "function" ? update(previous) : update,
        };
      }),
    [mediaId]
  );

  const navigate = useFixaNavigate();
  const goToMediaManagement = () => navigate("/launch-media");
  const { wizard } = useWizard({
    steps: STEPS,
    onComplete: async () => {
      // This is a placeholder for handling the completion of the wizard steps
      await Promise.resolve();
      goToMediaManagement();
    },
  });

  const enableLaunchMedia = useFlag(FLAGS.ENABLE_LAUNCH_MEDIA);
  if (!enableLaunchMedia) {
    return <Navigate to="/home" replace />;
  }

  if (!media) {
    return (
      <main className={mediaDetailPageSyles["page"]}>
        <NotFound
          title={t("mediaNotFoundString")}
          message={t("noResultsString")}
        />
      </main>
    );
  }

  const images = media.images?.length ? media.images : [media.imageUrl];
  const currentStepIsWorkBrief = wizard.currentStep === WizardStep.WorkBrief;
  const currentStepIsLastStep = wizard.currentStep === STEPS.at(-1);

  return (
    <main className={mediaDetailPageSyles["page"]}>
      <Linkbar items={linkbarItems} currentLabel={media.title} currentAsH1 />

      <div className={styles["backButtonContainer"]}>
        <BackButton
          className={styles["backButton"]}
          onClick={
            currentStepIsWorkBrief
              ? goToMediaManagement
              : wizard.goToPreviousStep
          }
        >
          {t("backToSearch", {
            initialPageTitle: t(getTranslationKeyByStep(wizard.currentStep)),
          })}
        </BackButton>
      </div>

      <section
        className={joinClassNames(
          mediaDetailPageSyles["pageContent"],
          styles["pageContent"]
        )}
      >
        <FixaText tagName="p">
          {t("stepNumberString", {
            number: wizard.currentStepIndex + 1,
          })}
        </FixaText>

        {currentStepIsWorkBrief && <WorkBriefStep images={images} />}
        {wizard.currentStep === WizardStep.ProductSelection && (
          <ProductSelectionStep
            media={media}
            backupProducts={backupProducts}
            onBackupProductsChange={handleBackupProductsChange}
          />
        )}
        {wizard.currentStep === WizardStep.DirectTakeaway && (
          <DirectTakeawayStep media={media} />
        )}
      </section>

      <section className={styles["footer"]}>
        <FixaButton
          className={styles["button"]}
          onClick={wizard.goToNextStep}
          text={currentStepIsLastStep ? t("saveString") : t("continue2String")}
          type="emphasised"
        />
      </section>
    </main>
  );
};
